<template>
  <Listbox v-model="locale" as="div">
    <div :class="compact ? 'relative' : 'relative mt-1'">
      <ListboxButton
        data-testid="locale-switcher"
        :class="[
          'infuse-locale-switcher relative text-left focus:outline-none focus:ring-1 focus:ring-primary-500',
          compact
            ? 'flex items-center rounded text-neutral-600 hover:text-secondary-700'
            : 'w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pl-3 pr-10 transition-colors duration-150 ease-out-quart hover:border-primary-500 sm:text-sm'
        ]"
        :aria-label="label ?? selectedLanguage?.label"
      >
        <span class="flex items-center">
          <span :class="compact ? 'flex flex-row items-center' : 'ml-3 flex flex-row gap-3 truncate'">
            <font-awesome-icon
              v-if="compact"
              :icon="['fal', 'globe']"
              class="infuse-locale-flag h-5 w-5"
              aria-hidden="true"
            />
            <img
              v-else
              class="infuse-locale-flag h-3 w-4 mt-1"
              :src="`${selectedLanguage?.flag}`"
              role="presentation"
              :alt="selectedLanguage?.label"
            />
            <span v-if="!compact"> {{ selectedLanguage?.label }}</span>
          </span>
        </span>
        <span
          v-if="!compact"
          class="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2"
        >
          <SelectorIcon class="infuse-locale-selector-icon h-5 w-5 text-gray-400" aria-hidden="true" />
        </span>
      </ListboxButton>
      <!--
        z-40, not z-10: table header rows are `sticky z-10` and the table filter bar is
        `relative z-30`. At an equal z-index the later element in the DOM wins, and the
        table always comes after the topbar — so a z-10 dropdown renders behind the
        headers. Stays below z-50 so it cannot cover a modal (SettingsModal hosts one).
      -->
      <ListboxOptions
        :class="[
          'absolute z-40 mt-1 max-h-56 overflow-auto rounded-md bg-white py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm',
          compact ? 'w-48 right-0' : 'w-full'
        ]"
      >
        <ListboxOption
          v-for="language in languages"
          :key="language.id"
          v-slot="{ active, selected }"
          as="template"
          :value="language.value"
          @click="setLocalSelector()"
        >
          <li
            :data-testid="`locale-option-${language.value}`"
            :class="[
              active ? 'bg-primary-500 text-white' : 'text-gray-900',
              'relative cursor-default select-none py-2 pl-3 pr-9'
            ]"
          >
            <span :class="[selected ? 'font-semibold' : 'font-normal', 'ml-3 flex flex-row gap-3 truncate']">
              <img class="h-3 w-4 mt-1" :src="`${language.flag}`" role="presentation" :alt="language.label" />

              <span> {{ language.label }}</span>
            </span>
            <span
              v-if="selected"
              :class="[active ? 'text-white' : 'text-primary-500', 'absolute inset-y-0 right-0 flex items-center pr-4']"
            >
              <CheckIcon class="h-5 w-5" aria-hidden="true" />
            </span>
          </li>
        </ListboxOption>
      </ListboxOptions>
    </div>
  </Listbox>
</template>
<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid';
import { languages as globalLanguages } from '@infuse/ui/utils';

export default defineComponent({
  name: 'LocaleSwitcher',
  components: {
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    SelectorIcon,
    CheckIcon
  },

  props: {
    /** Icon-only trigger, for a compact topbar. Defaults to the full select. */
    compact: { type: Boolean, default: false },
    /** Accessible name for the trigger button. Optional: other consumers are unaffected. */
    label: { type: String, default: undefined }
  },

  setup() {
    const { locale } = useI18n({ useScope: 'global' });

    const supportedLanguages = ['nl', 'en', 'de', 'fr', 'es', 'it', 'pt', 'pl'];
    const languages = globalLanguages.filter((lang) => supportedLanguages.includes(lang.value));

    // Must be computed: the locale can change from anywhere (another switcher, a
    // restored localStorage value), and a one-shot lookup leaves the trigger stale.
    const selectedLanguage = computed(() => languages.find((lang) => lang.value === locale.value));

    return {
      locale,
      languages: languages,
      selectedLanguage
    };
  },
  methods: {
    setLocalSelector() {
      localStorage.setItem('locale', this.locale);
      document.documentElement.setAttribute('lang', this.locale);
    }
  }
});
</script>

<style scoped>
.locale-select {
  width: 150px;
}
</style>
